import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_kaur-studio/artifacts/33hz2s76_1000036423.jpg" 
              alt="Nanaki Kaur Logo" 
              className="h-14 w-auto object-contain bg-white rounded-lg px-2 py-1"
            />
            <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
              Nanaki Kaur
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-purple-600 transition">
              Home
            </Link>
            <Link to="/products" className="text-gray-700 hover:text-purple-600 transition">
              Products
            </Link>
            <Link to="/our-philosophy" className="text-gray-700 hover:text-purple-600 transition" data-testid="nav-our-philosophy">
              Our Philosophy
            </Link>
            <Link to="/how-nanaki-works" className="text-gray-700 hover:text-purple-600 transition" data-testid="nav-how-nanaki-works">
              How It Works
            </Link>
            <Link to="/register-angel" className="text-gray-700 hover:text-purple-600 transition" data-testid="nav-become-kaur">
              Become a Kaur
            </Link>
            <Link to="/showcase-brand" className="text-gray-700 hover:text-purple-600 transition" data-testid="nav-showcase-brand">
              Showcase Brand
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-purple-600 transition">
              About
            </Link>
            
            {user && user.role === 'angel' && (
              <Link to="/angel-dashboard" className="text-purple-600 font-semibold hover:text-purple-700 transition">
                Kaur Dashboard
              </Link>
            )}
            
            {user && user.role === 'admin' && (
              <Link to="/admin/dashboard" className="text-purple-600 font-semibold hover:text-purple-700 transition">
                Admin Panel
              </Link>
            )}
          </div>

          {/* Right Side Icons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/cart" className="text-gray-700 hover:text-purple-600 transition">
              <ShoppingCart className="w-6 h-6" />
            </Link>
            
            {user ? (
              <div className="flex items-center space-x-3">
                <Link to="/dashboard" className="text-gray-700 hover:text-purple-600 transition">
                  <User className="w-6 h-6" />
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-purple-600 transition"
                  title="Logout"
                >
                  <LogOut className="w-6 h-6" />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-3">
            <Link
              to="/"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/products"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              Products
            </Link>
            <Link
              to="/our-philosophy"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-our-philosophy"
            >
              Our Philosophy
            </Link>
            <Link
              to="/how-nanaki-works"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-how-nanaki-works"
            >
              How It Works
            </Link>
            <Link
              to="/register-angel"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-become-kaur"
            >
              Become a Kaur
            </Link>
            <Link
              to="/showcase-brand"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-showcase-brand"
            >
              Showcase Brand
            </Link>
            <Link
              to="/about"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/cart"
              className="block text-gray-700 hover:text-purple-600 transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              Cart
            </Link>
            
            {user && user.role === 'angel' && (
              <Link
                to="/angel-dashboard"
                className="block text-purple-600 font-semibold hover:text-purple-700 transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Kaur Dashboard
              </Link>
            )}
            
            {user && user.role === 'admin' && (
              <Link
                to="/admin/dashboard"
                className="block text-purple-600 font-semibold hover:text-purple-700 transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin Panel
              </Link>
            )}
            
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block text-gray-700 hover:text-purple-600 transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Account
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-gray-700 hover:text-purple-600 transition"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
